/**
 * Org Roster Screen — Phase 2B
 *
 * Route: /org-roster?orgId=<uuid>
 *
 * Layout:
 *   - Active members (role badge, remove button for owner/admin)
 *   - Pending invites (email, role, revoke + copy link for owner/admin)
 *   - Pending claims (claim_pending memberships — read-only, approve/reject for owner/admin)
 *   - Invite button → inline modal
 *
 * Permissions:
 *   - owner / admin : full access (invite, revoke, remove, approve claims)
 *   - coach         : read-only (can see roster, cannot modify)
 *   - member        : access denied
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md — organizations canonical table only.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Pressable,
  SafeAreaView,
  ScrollView,
  ActivityIndicator,
  Alert,
  Modal,
  TextInput,
  StyleSheet,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  createOrgInvite,
  listOrgInvitesForOrg,
  revokeOrgInvite,
  buildInviteLink,
} from '@/lib/orgInvites';
import { fetchOrganization, getOrgRoleForUser } from '@/lib/organizations';
import { TYPO } from '@/constants/typography';
import type { OrgInvite, OrgRole, Organization } from '@/types/database';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActiveMember {
  id: string;          // membership id
  user_id: string;
  role: OrgRole;
  display_name: string;
  handle: string | null;
}

interface PendingClaim {
  id: string;          // membership id
  user_id: string;
  role: OrgRole;
  display_name: string;
  handle: string | null;
}

type InviteRole = 'member' | 'coach' | 'admin';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  coach: 'Coach',
  member: 'Member',
};

const ROLE_OPTIONS: { value: InviteRole; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'coach', label: 'Coach' },
  { value: 'admin', label: 'Admin' },
];

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function OrgRosterScreen() {
  const { orgId } = useLocalSearchParams<{ orgId: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const { session } = useAuth();
  const c = theme.colors;
  const userId = session?.user?.id ?? '';

  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState<Organization | null>(null);
  const [myRole, setMyRole] = useState<OrgRole | null>(null);
  const [members, setMembers] = useState<ActiveMember[]>([]);
  const [claims, setClaims] = useState<PendingClaim[]>([]);
  const [invites, setInvites] = useState<OrgInvite[]>([]);
  const [accessDenied, setAccessDenied] = useState(false);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRole>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ link: string } | null>(null);

  const isAdminOrOwner = myRole === 'owner' || myRole === 'admin';

  // ── Load data ───────────────────────────────────────────────────────────
  const loadRoster = useCallback(async () => {
    if (!orgId || !userId) return;

    setLoading(true);
    try {
      // 1. Org details
      const fetchedOrg = await fetchOrganization(orgId);
      setOrg(fetchedOrg);

      // 2. User's role
      const role = await getOrgRoleForUser(orgId, userId);
      setMyRole(role);

      // 3. Access check: member has no roster access
      if (role === 'member' || role === null) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // 4. Active members (exclude claim_pending; include all active roles)
      const { data: memberRows } = await supabase
        .from('organization_memberships')
        .select(`
          id,
          user_id,
          role,
          profiles!inner(display_name, handle)
        `)
        .eq('org_id', orgId)
        .eq('status', 'active')
        .neq('role', 'claim_pending')
        .order('created_at', { ascending: true });

      setMembers(
        (memberRows ?? []).map((m: any) => ({
          id: m.id,
          user_id: m.user_id,
          role: m.role as OrgRole,
          display_name: m.profiles?.display_name ?? 'Unknown',
          handle: m.profiles?.handle ?? null,
        })),
      );

      // 5. Pending claims (claim_pending status)
      const { data: claimRows } = await supabase
        .from('organization_memberships')
        .select(`
          id,
          user_id,
          role,
          profiles!inner(display_name, handle)
        `)
        .eq('org_id', orgId)
        .eq('status', 'claim_pending')
        .order('created_at', { ascending: true });

      setClaims(
        (claimRows ?? []).map((m: any) => ({
          id: m.id,
          user_id: m.user_id,
          role: m.role as OrgRole,
          display_name: m.profiles?.display_name ?? 'Unknown',
          handle: m.profiles?.handle ?? null,
        })),
      );

      // 6. Pending invites
      const fetchedInvites = await listOrgInvitesForOrg(orgId);
      setInvites(fetchedInvites.filter((i) => i.status === 'pending'));
    } finally {
      setLoading(false);
    }
  }, [orgId, userId]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  // ── Remove member ────────────────────────────────────────────────────────
  const handleRemoveMember = (member: ActiveMember) => {
    if (!isAdminOrOwner) return;
    if (member.user_id === userId) {
      Alert.alert('Cannot remove yourself', 'You cannot remove yourself from your own organization.');
      return;
    }
    Alert.alert(
      'Remove Member',
      `Remove ${member.display_name} from ${org?.name ?? 'this organization'}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            const { error } = await supabase
              .from('organization_memberships')
              .update({ status: 'inactive' })
              .eq('id', member.id);

            if (error) {
              Alert.alert('Error', 'Failed to remove member. Please try again.');
            } else {
              await loadRoster();
            }
          },
        },
      ],
    );
  };

  // ── Revoke invite ────────────────────────────────────────────────────────
  const handleRevokeInvite = async (invite: OrgInvite) => {
    if (!isAdminOrOwner) return;
    try {
      await revokeOrgInvite(invite.id, userId);
      await loadRoster();
    } catch (err: any) {
      Alert.alert('Error', err?.message ?? 'Failed to revoke invite.');
    }
  };

  // ── Copy invite link ─────────────────────────────────────────────────────
  const handleCopyLink = async (token: string) => {
    const link = buildInviteLink(token);
    await Clipboard.setStringAsync(link);
    Alert.alert('Copied!', `Invite link copied to clipboard:\n\n${link}`);
  };

  // ── Approve / Reject claim ───────────────────────────────────────────────
  const handleApproveClaim = async (claim: PendingClaim) => {
    if (!isAdminOrOwner) return;
    const { error } = await supabase
      .from('organization_memberships')
      .update({ status: 'active' })
      .eq('id', claim.id);

    if (error) {
      Alert.alert('Error', 'Failed to approve claim.');
    } else {
      await loadRoster();
    }
  };

  const handleRejectClaim = (claim: PendingClaim) => {
    if (!isAdminOrOwner) return;
    Alert.alert('Reject Claim', `Reject ${claim.display_name}'s claim?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Reject',
        style: 'destructive',
        onPress: async () => {
          const { error } = await supabase
            .from('organization_memberships')
            .update({ status: 'inactive' })
            .eq('id', claim.id);

          if (error) {
            Alert.alert('Error', 'Failed to reject claim.');
          } else {
            await loadRoster();
          }
        },
      },
    ]);
  };

  // ── Create invite ────────────────────────────────────────────────────────
  const handleCreateInvite = async () => {
    const trimmedEmail = inviteEmail.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      Alert.alert('Invalid email', 'Please enter a valid email address.');
      return;
    }

    setInviting(true);
    try {
      const newInvite = await createOrgInvite(orgId!, userId, trimmedEmail, inviteRole);
      const link = buildInviteLink(newInvite.token);
      setInviteResult({ link });
      setInviteEmail('');
      await loadRoster();
    } catch (err: any) {
      Alert.alert('Error', err?.message ?? 'Failed to create invite.');
    } finally {
      setInviting(false);
    }
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('member');
    setInviteResult(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const styles = makeStyles(c);

  if (loading) {
    return (
      <SafeAreaView style={styles.centered}>
        <ActivityIndicator color={c.accent} />
      </SafeAreaView>
    );
  }

  if (accessDenied) {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.heading}>Access Denied</Text>
        <Text style={styles.subText}>
          Only org coaches, admins, and owners can view the roster.
        </Text>
        <Pressable style={styles.btnSecondary} onPress={() => router.back()}>
          <Text style={styles.btnSecondaryText}>Go Back</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
      {/* Header */}
      <View style={styles.headerRow}>
        <Pressable
          onPress={() =>
            router.push({
              pathname: '/club-home' as any,
              params: { orgId: orgId! },
            })
          }
          style={styles.backBtn}
        >
          <Text style={styles.backBtnText}>🏠 Club</Text>
        </Pressable>
        <Text style={styles.title}>{org?.name ?? 'Roster'}</Text>
        <View style={styles.headerActions}>
          <Pressable
            style={styles.leaderboardBtn}
            onPress={() =>
              router.push({
                pathname: '/org-leaderboard' as any,
                params: { orgId: orgId! },
              })
            }
          >
            <Text style={styles.leaderboardBtnText}>🏆 Leaderboard</Text>
          </Pressable>
          {isAdminOrOwner && (
            <Pressable
              style={styles.inviteBtn}
              onPress={() => setShowInviteModal(true)}
            >
              <Text style={styles.inviteBtnText}>+ Invite</Text>
            </Pressable>
          )}
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>

        {/* ── Active Members ── */}
        <Text style={styles.sectionLabel}>Active Members ({members.length})</Text>
        {members.length === 0 ? (
          <Text style={styles.emptyText}>No active members yet.</Text>
        ) : (
          members.map((member) => (
            <View key={member.id} style={styles.row}>
              <View style={styles.rowMain}>
                <Text style={styles.memberName}>{member.display_name}</Text>
                {member.handle ? (
                  <Text style={styles.memberHandle}>@{member.handle}</Text>
                ) : null}
              </View>
              <View style={styles.rowActions}>
                <View style={styles.rolePill}>
                  <Text style={styles.rolePillText}>{ROLE_LABELS[member.role] ?? member.role}</Text>
                </View>
                {isAdminOrOwner && member.user_id !== userId && (
                  <Pressable
                    style={styles.removeBtn}
                    onPress={() => handleRemoveMember(member)}
                  >
                    <Text style={styles.removeBtnText}>Remove</Text>
                  </Pressable>
                )}
              </View>
            </View>
          ))
        )}

        {/* ── Pending Invites ── */}
        {isAdminOrOwner && (
          <>
            <Text style={[styles.sectionLabel, { marginTop: 28 }]}>
              Pending Invites ({invites.length})
            </Text>
            {invites.length === 0 ? (
              <Text style={styles.emptyText}>No pending invites.</Text>
            ) : (
              invites.map((invite) => (
                <View key={invite.id} style={styles.row}>
                  <View style={styles.rowMain}>
                    <Text style={styles.memberName}>{invite.email}</Text>
                    <Text style={styles.memberHandle}>
                      Sent {new Date(invite.created_at).toLocaleDateString()} ·{' '}
                      Expires {new Date(invite.expires_at).toLocaleDateString()}
                    </Text>
                  </View>
                  <View style={styles.rowActions}>
                    <View style={styles.rolePill}>
                      <Text style={styles.rolePillText}>{ROLE_LABELS[invite.role] ?? invite.role}</Text>
                    </View>
                    <Pressable
                      style={styles.copyBtn}
                      onPress={() => handleCopyLink(invite.token)}
                    >
                      <Text style={styles.copyBtnText}>Copy Link</Text>
                    </Pressable>
                    <Pressable
                      style={styles.removeBtn}
                      onPress={() => handleRevokeInvite(invite)}
                    >
                      <Text style={styles.removeBtnText}>Revoke</Text>
                    </Pressable>
                  </View>
                </View>
              ))
            )}
          </>
        )}

        {/* ── Pending Claims ── */}
        {isAdminOrOwner && claims.length > 0 && (
          <>
            <Text style={[styles.sectionLabel, { marginTop: 28 }]}>
              Pending Ownership Claims ({claims.length})
            </Text>
            <Text style={styles.claimsNote}>
              These users have requested to claim this organization.
            </Text>
            {claims.map((claim) => (
              <View key={claim.id} style={styles.row}>
                <View style={styles.rowMain}>
                  <Text style={styles.memberName}>{claim.display_name}</Text>
                  {claim.handle ? (
                    <Text style={styles.memberHandle}>@{claim.handle} · as {ROLE_LABELS[claim.role] ?? claim.role}</Text>
                  ) : null}
                </View>
                <View style={styles.rowActions}>
                  <Pressable
                    style={styles.approveBtn}
                    onPress={() => handleApproveClaim(claim)}
                  >
                    <Text style={styles.approveBtnText}>Approve</Text>
                  </Pressable>
                  <Pressable
                    style={styles.removeBtn}
                    onPress={() => handleRejectClaim(claim)}
                  >
                    <Text style={styles.removeBtnText}>Reject</Text>
                  </Pressable>
                </View>
              </View>
            ))}
          </>
        )}

        {/* Bottom padding */}
        <View style={{ height: 40 }} />
      </ScrollView>

      {/* ── Invite Modal ── */}
      <Modal
        visible={showInviteModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={handleCloseInviteModal}
      >
        <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Invite Member</Text>
            <Pressable onPress={handleCloseInviteModal}>
              <Text style={styles.modalClose}>Done</Text>
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={styles.modalContent}>
            {inviteResult ? (
              /* ── Success state ── */
              <View style={styles.successBox}>
                <Text style={styles.successTitle}>✅ Invite Created!</Text>
                <Text style={styles.successSub}>Share this link with the person you invited:</Text>
                <View style={styles.linkBox}>
                  <Text style={styles.linkText} selectable>{inviteResult.link}</Text>
                </View>
                <Pressable
                  style={styles.btnPrimary}
                  onPress={() => Clipboard.setStringAsync(inviteResult.link).then(() =>
                    Alert.alert('Copied!', 'Invite link copied to clipboard.'),
                  )}
                >
                  <Text style={styles.btnPrimaryText}>Copy Link</Text>
                </Pressable>
                <Pressable
                  style={[styles.btnSecondary, { marginTop: 8 }]}
                  onPress={() => setInviteResult(null)}
                >
                  <Text style={styles.btnSecondaryText}>Invite Another</Text>
                </Pressable>
              </View>
            ) : (
              /* ── Form state ── */
              <>
                <Text style={styles.inputLabel}>Email Address</Text>
                <TextInput
                  value={inviteEmail}
                  onChangeText={setInviteEmail}
                  placeholder="athlete@example.com"
                  placeholderTextColor={c.textTertiary}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  style={styles.input}
                />

                <Text style={[styles.inputLabel, { marginTop: 20 }]}>Role</Text>
                {ROLE_OPTIONS.map((opt) => (
                  <Pressable
                    key={opt.value}
                    style={[
                      styles.roleOption,
                      inviteRole === opt.value && styles.roleOptionSelected,
                    ]}
                    onPress={() => setInviteRole(opt.value)}
                  >
                    <Text style={[
                      styles.roleOptionText,
                      inviteRole === opt.value && styles.roleOptionTextSelected,
                    ]}>
                      {opt.label}
                    </Text>
                  </Pressable>
                ))}

                <Pressable
                  style={[styles.btnPrimary, { marginTop: 28 }]}
                  onPress={handleCreateInvite}
                  disabled={inviting}
                >
                  {inviting ? (
                    <ActivityIndicator color={c.accentForeground} />
                  ) : (
                    <Text style={styles.btnPrimaryText}>Send Invite</Text>
                  )}
                </Pressable>
              </>
            )}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

function makeStyles(c: any) {
  return StyleSheet.create({
    centered: {
      flex: 1,
      backgroundColor: c.background,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 32,
    },
    heading: {
      fontSize: 22,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      textAlign: 'center',
      marginBottom: 12,
    },
    subText: {
      fontSize: 15,
      color: c.textSecondary,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 24,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    backBtn: { padding: 4 },
    backBtnText: {
      color: c.accent,
      fontSize: 15,
      fontFamily: TYPO.body.regular,
    },
    title: {
      fontSize: 17,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      flex: 1,
      textAlign: 'center',
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    leaderboardBtn: {
      borderRadius: 6,
      borderWidth: 1,
      borderColor: c.border,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    leaderboardBtnText: {
      color: c.text,
      fontSize: 13,
      fontFamily: TYPO.body.semibold,
    },
    inviteBtn: {
      backgroundColor: c.accent,
      borderRadius: 6,
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    inviteBtnText: {
      color: c.accentForeground,
      fontSize: 14,
      fontFamily: TYPO.body.semibold,
    },
    content: {
      padding: 16,
    },
    sectionLabel: {
      fontSize: 12,
      fontFamily: TYPO.body.semibold,
      color: c.textSecondary,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      marginBottom: 12,
    },
    emptyText: {
      fontSize: 14,
      color: c.textTertiary,
      marginBottom: 8,
    },
    claimsNote: {
      fontSize: 13,
      color: c.textSecondary,
      marginBottom: 8,
      lineHeight: 18,
    },
    row: {
      backgroundColor: c.surface,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: c.border,
      padding: 12,
      marginBottom: 8,
      gap: 8,
    },
    rowMain: { flex: 1 },
    memberName: {
      fontSize: 15,
      fontFamily: TYPO.body.semibold,
      color: c.text,
    },
    memberHandle: {
      fontSize: 12,
      color: c.textSecondary,
      marginTop: 2,
    },
    rowActions: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 6,
    },
    rolePill: {
      backgroundColor: c.accent + '18',
      borderRadius: 4,
      paddingHorizontal: 8,
      paddingVertical: 3,
    },
    rolePillText: {
      fontSize: 11,
      fontFamily: TYPO.body.semibold,
      color: c.accent,
    },
    removeBtn: {
      borderRadius: 4,
      borderWidth: 1,
      borderColor: '#ef4444',
      paddingHorizontal: 8,
      paddingVertical: 3,
    },
    removeBtnText: {
      fontSize: 11,
      color: '#ef4444',
      fontFamily: TYPO.body.semibold,
    },
    approveBtn: {
      borderRadius: 4,
      borderWidth: 1,
      borderColor: '#22c55e',
      paddingHorizontal: 8,
      paddingVertical: 3,
    },
    approveBtnText: {
      fontSize: 11,
      color: '#22c55e',
      fontFamily: TYPO.body.semibold,
    },
    copyBtn: {
      borderRadius: 4,
      borderWidth: 1,
      borderColor: c.border,
      paddingHorizontal: 8,
      paddingVertical: 3,
    },
    copyBtnText: {
      fontSize: 11,
      color: c.textSecondary,
      fontFamily: TYPO.body.semibold,
    },
    btnPrimary: {
      backgroundColor: c.accent,
      borderRadius: 6,
      padding: 16,
      alignItems: 'center',
    },
    btnPrimaryText: {
      color: c.accentForeground,
      fontSize: 16,
      fontFamily: TYPO.heading.bold,
    },
    btnSecondary: {
      borderRadius: 6,
      borderWidth: 1.5,
      borderColor: c.border,
      padding: 14,
      alignItems: 'center',
    },
    btnSecondaryText: {
      color: c.text,
      fontSize: 15,
      fontFamily: TYPO.body.regular,
    },
    // Modal
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    modalTitle: {
      fontSize: 18,
      fontFamily: TYPO.heading.bold,
      color: c.text,
    },
    modalClose: {
      color: c.accent,
      fontSize: 16,
      fontFamily: TYPO.body.regular,
    },
    modalContent: {
      padding: 20,
      paddingBottom: 48,
    },
    inputLabel: {
      fontSize: 12,
      fontFamily: TYPO.body.semibold,
      color: c.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 8,
    },
    input: {
      backgroundColor: c.surface,
      borderWidth: 1.5,
      borderColor: c.border,
      borderRadius: 6,
      padding: 14,
      fontSize: 15,
      color: c.text,
      fontFamily: TYPO.body.regular,
    },
    roleOption: {
      backgroundColor: c.surface,
      borderWidth: 1.5,
      borderColor: c.border,
      borderRadius: 6,
      padding: 14,
      marginBottom: 8,
    },
    roleOptionSelected: {
      borderColor: c.accent,
      backgroundColor: c.accent + '18',
    },
    roleOptionText: {
      fontSize: 15,
      color: c.text,
      fontFamily: TYPO.body.regular,
    },
    roleOptionTextSelected: {
      fontFamily: TYPO.body.semibold,
      color: c.accent,
    },
    successBox: {
      alignItems: 'center',
      paddingTop: 16,
    },
    successTitle: {
      fontSize: 20,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      marginBottom: 8,
    },
    successSub: {
      fontSize: 14,
      color: c.textSecondary,
      textAlign: 'center',
      marginBottom: 16,
      lineHeight: 20,
    },
    linkBox: {
      backgroundColor: c.surface,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: c.border,
      padding: 12,
      marginBottom: 16,
      width: '100%',
    },
    linkText: {
      fontSize: 13,
      color: c.text,
      fontFamily: TYPO.body.regular,
    },
  });
}
